import React, { useEffect, useState, useRef } from "react";
import LeadDbBulkHistoryService from "../components/Services/LeadDbBulkHistoryService";
import { userId, userRole } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { getBasePathByUserRole } from "../utils/roleRoutes";
import { showErrorToast, showInfoToast } from "../components/Constant/Notification";
import { formatDate } from "../components/Constant/Constant";

const useLeadBulkHistory = () => {
  const [leadBulkHistory, setLeadBulkHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [quality, setQuality] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [visible, setVisible] = useState(false);
  const [pageTokens, setPageTokens] = useState([null]);
  const [clearClickCount, setClearClickCount] = useState(0);
  const navigate = useNavigate();

  const historyCache = useRef({});

  const formatDateOnly = (dateStr) => {
    return dateStr ? new Date(dateStr).toISOString().split("T")[0] : null;
  };

  const getLeadBulkHistory = async ({ pageToken, pageSize, direction, page }) => {
    const cacheKey = `${pageSize}-${pageToken || "initial"}-${formatDateOnly(startDate)}-${formatDateOnly(endDate)}-${quality?.label || "no-quality"}`;

    // Check cache first
    if (historyCache.current[cacheKey]) {
      const cached = historyCache.current[cacheKey];
      setLeadBulkHistory(cached.bulkUploadHistories);
      setNextPageToken(cached.next);
      setPrevPageToken(cached.prev);
      setCurrentPage(page);
      setHasMore(!!cached.next);
      return;
    }


    setLoading(true);
    try {
      const body = {
        limit: pageSize,
        startDate: startDate ? formatDate(startDate) : null || "",
        endDate: endDate ? formatDate(endDate) : null || "",
        userId: userRole === "BOD" ? userId : null,
        quality: quality?.label || null,
        next: pageToken,
        direction: direction,
      };

      const response = await LeadDbBulkHistoryService.getLeadDbBulkHistory(body);
      const data = response?.data;
      const nextToken = data?.next || null;
      const prevToken = data?.prev || null;

      setIsFilterOpen(false);
      setLeadBulkHistory(data?.bulkUploadHistories || []);
      setNextPageToken(nextToken);
      setPrevPageToken(prevToken);
      setCurrentPage(page);
      setHasMore(!!nextToken);

      // Cache the response
      historyCache.current[cacheKey] = {
        bulkUploadHistories: data?.bulkUploadHistories || [],
        next: nextToken,
        prev: prevToken,
      };

      // Update pageTokens
      if (direction === "next" && nextToken) {

        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length === page) {
            newTokens.push(nextToken);
          } else {
            newTokens[page] = nextToken;
          }
          return newTokens;
        });
      } else if (direction === "previous" && prevToken && page > 1) {
        setPageTokens((prev) => {
          const newTokens = [...prev];
          if (newTokens.length >= page && newTokens[page - 1] !== prevToken) {
            newTokens[page - 1] = prevToken;
          }
          return newTokens;
        });
      }
    } catch (error) {
      console.error("Error fetching lead bulk history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeadBulkHistory({ pageSize, pageToken: null, page: 1 });
  }, []);

  const handlePageSizeChange = async (size) => {
    setPageSize(size);
    setCurrentPage(1);
    setPageTokens([null]);
    historyCache.current = {};
    await getLeadBulkHistory({ pageSize: size, pageToken: null, page: 1 });
  };

  const handlePageChange = async (token, direction) => {
    console.log("Page change requested:", { direction, currentPage });

    if (direction === "next" && hasMore) {
      const newPage = currentPage + 1;
      await getLeadBulkHistory({
        pageToken: nextPageToken,
        pageSize,
        direction: "next",
        page: newPage,
      });
    } else if (direction === "previous" && currentPage > 1) {
      const newPage = currentPage - 1;
      const prevToken = pageTokens[newPage - 1] || null;
      const cacheKey = `${pageSize}-${prevToken || "initial"}-${formatDateOnly(startDate)}-${formatDateOnly(endDate)}-${quality?.label || "no-quality"}`;

     
      if (historyCache.current[cacheKey]) {
        const cached = historyCache.current[cacheKey];
        setLeadBulkHistory(cached.bulkUploadHistories);
        setNextPageToken(cached.next);
        setPrevPageToken(cached.prev);
        setCurrentPage(newPage);
        setHasMore(!!cached.next);
      } else {
       
        await getLeadBulkHistory({
          pageToken: prevToken,
          pageSize,
          direction: "prev",
          page: newPage,
        });
      }
    }
  };

  const handleToggelFilter = () => {
    setIsFilterOpen(true);
  };

  const handleApplyFilter = ({ quality, startDate, endDate }) => {

 
        if (startDate && !endDate) {
          showErrorToast("End Date is required when Start Date is selected.");
          return;
        }
    setQuality(quality);
    setStartDate(startDate);
    setEndDate(endDate);
    setCurrentPage(1);
    setPageTokens([null]);
    historyCache.current = {};
    getLeadBulkHistory({ pageSize, pageToken: null, page: 1 });
  };

  const clearFilters = () => {
    setQuality("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
    setPageTokens([null]);
    historyCache.current = {};
    setClearClickCount((prev) => {
      const newCount = prev + 1;
      if (newCount === 2) {
        getLeadBulkHistory({ pageSize, pageToken: null, page: 1 });
        return 0;
      }
      return newCount;
    });
  };

  const path = getBasePathByUserRole();
  const handleOnViewLeads = (batchId) => {
    navigate(`${path}/lead-bulk-upload-data`, {
      state: { batchId },
    });
  };

  return {
    leadBulkHistory,
    getLeadBulkHistory,
    handlePageSizeChange,
    handlePageChange,
    pageSize,
    currentPage,
    nextPageToken,
    prevPageToken,
    hasMore,
    loading,
    setIsFilterOpen,
    isFilterOpen,
    quality,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handleApplyFilter,
    handleToggelFilter,
    clearFilters,
    setQuality,
    formData,
    setFormData,
    handleOnViewLeads,
    visible,
    setVisible,
  };
};

export default useLeadBulkHistory;